import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";
import { authClient } from "@/frontend/auth-client";

export function useUpdateUserMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newName: string) => {
      const result = await authClient.updateUser({ name: newName });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [["session"]] });
      queryClient.invalidateQueries({ queryKey: orpc.org.listMembers.key() });
      if (onSuccess) onSuccess();
    },
  });
}

export function useUploadMemberImageMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.org.uploadMemberImage.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.org.getMyMemberProfile.key() });
        queryClient.invalidateQueries({ queryKey: orpc.org.listMembers.key() });
      },
    })
  );
}


export function useDeleteOwnAccountMutation() {
  return useMutation(orpc.admin.deleteOwnAccount.mutationOptions());
}

export function useUpdateOrganizationMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.org.updateOrganization.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.org.getFullOrganization.key() });
        queryClient.invalidateQueries({ queryKey: orpc.org.listOrganizations.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useUploadOrgLogoMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.org.uploadOrgLogo.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.org.getFullOrganization.key() });
        queryClient.invalidateQueries({ queryKey: orpc.org.listOrganizations.key() });
      },
    })
  );
}

export function useDeleteOrganizationMutation(onSuccess?: (result: { signedOut: boolean }) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.org.deleteOrganization.mutationOptions({
      onSuccess: (result) => {
        queryClient.invalidateQueries();
        if (onSuccess) onSuccess(result);
      },
    })
  );
}

export function useCreateInvitationMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.createInvitation.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listInvitations.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useUpdateMemberRoleMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.org.updateMemberRole.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.org.listMembers.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useRemoveMemberMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.org.removeMember.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.org.listMembers.key() });
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUsers.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useDeleteInvitationMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.deleteInvitation.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listInvitations.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useCreateUserMutation(onSuccess?: (data: { id: string }) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.createUser.mutationOptions({
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUsers.key() });
        if (onSuccess) onSuccess(data);
      },
    })
  );
}

/** Toggles a user's beta flag (platform-admin only); resyncs the admin user list on success or failure. */
export function useSetUserBetaFlagMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.setUserBetaFlag.mutationOptions({
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUsers.key() });
      },
    })
  );
}

/** Pre-provisions a user from email + beta flag; refreshes the admin user list. */
export function usePreprovisionUserMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.preprovisionUser.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUsers.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useDeleteUserMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.deleteUser.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUsers.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useUpdateUserRoleMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.updateUserRole.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUsers.key() });
      },
    })
  );
}

export function useSetUserBannedMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.setUserBanned.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listUsers.key() });
      },
    })
  );
}

export function useCreateApiKeyMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.createApiKey.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listApiKeys.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useUpsertEhrClientIdMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.upsertEhrClientId.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listEhrClientIds.key() });
        queryClient.invalidateQueries({ queryKey: orpc.fhirSources.search.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useDeleteEhrClientIdMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.deleteEhrClientId.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listEhrClientIds.key() });
        queryClient.invalidateQueries({ queryKey: orpc.fhirSources.search.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useUpsertClinicalWorkflowMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.upsertClinicalWorkflow.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listClinicalWorkflows.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useDisableClinicalWorkflowMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.disableClinicalWorkflow.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listClinicalWorkflows.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

/** Invalidate every backend config query (all providerType inputs). */
function invalidateBackend(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: orpc.admin.getBackend.key() });
}

/** Persist the full staged backend config (client ID + sources + Group IDs). */
export function useSaveBackendMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.saveBackend.mutationOptions({
      onSuccess: () => { invalidateBackend(queryClient); if (onSuccess) onSuccess(); },
    })
  );
}

export function useDisableBackendMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.disableBackend.mutationOptions({
      onSuccess: () => invalidateBackend(queryClient),
    })
  );
}

export function useRevokeApiKeyMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.admin.revokeApiKey.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listApiKeys.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useCreateWebhookMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.webhooks.createWebhook.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.webhooks.listWebhooks.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useUpdateWebhookMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.webhooks.updateWebhook.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.webhooks.listWebhooks.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useDeleteWebhookMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.webhooks.deleteWebhook.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.webhooks.listWebhooks.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useRotateWebhookSecretMutation(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.webhooks.rotateSecret.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.webhooks.listWebhooks.key() });
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useRedeliverWebhookEventMutation(endpointId: string | null, onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.webhooks.redeliverEvent.mutationOptions({
      onSuccess: () => {
        if (endpointId) {
          queryClient.invalidateQueries({
            queryKey: orpc.webhooks.listEvents.key({ input: { id: endpointId } }),
          });
        }
        if (onSuccess) onSuccess();
      },
    })
  );
}

export function useRevokeConnectedAppMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.connectedApps.revoke.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.connectedApps.list.key() });
      },
    })
  );
}
